import { useLocale, useTranslations } from 'next-intl'
import { ComponentProps } from 'react'

import type { TRoomDetail } from '@/entities/room'
import { cn } from '@/shared/lib'
import { Icon, Separator } from '@/shared/ui'

import { Discounts } from '../common'

type Props = ComponentProps<'details'> &
  Pick<TRoomDetail, 'longTermDiscounts' | 'earlyCheckinDiscounts'> & {
    showSeparator?: boolean
  }

export const DiscountInformation = ({
  longTermDiscounts,
  earlyCheckinDiscounts,
  showSeparator = false,
  className,
  ...restProps
}: Props) => {
  const locale = useLocale()
  const t = useTranslations()

  const isEnglish = locale === 'en'

  const hasLongTermDiscount = longTermDiscounts.length > 0
  const hasEarlyCheckinDiscount = earlyCheckinDiscounts.length > 0

  const hasNoDiscount = !hasLongTermDiscount && !hasEarlyCheckinDiscount
  const hasAllDiscount = hasLongTermDiscount && hasEarlyCheckinDiscount

  if (hasNoDiscount) {
    return null
  }

  const maxLongTermDiscountRate = longTermDiscounts.at(-1)?.discountRate || null // 기간이 늘어날수록 더 큰 할인율 적용
  const maxEarlyCheckinDiscountAmount =
    earlyCheckinDiscounts[0]?.discountAmount || null // 빨리 입주할수록 더 큰 할인 금액 적용

  return (
    <>
      {showSeparator && <Separator color="light-gray" />}
      <details className={cn('group px-6', className)} {...restProps}>
        <summary
          className={cn(
            'flex cursor-pointer list-none gap-1 select-none',
            isEnglish && 'items-end',
          )}
        >
          <dl
            className={cn(
              'typo-label1 flex w-full items-center justify-between gap-3',
              isEnglish && 'flex-col items-start gap-0',
            )}
          >
            <dt className="font-normal text-gray-500">
              {t('room_detail.discount.title')}
            </dt>
            <dd className="flex items-center gap-2">
              {hasLongTermDiscount && (
                <div
                  className={cn('flex items-center', {
                    "after:block after:w-[11px] after:text-center after:text-xs after:font-extralight after:text-purple-300 after:content-['|']":
                      hasAllDiscount,
                  })}
                >
                  <span className="mr-1 block font-normal text-gray-900">
                    {t('room_detail.long_term')}
                  </span>
                  <span className="mr-2 block font-semibold text-black">
                    ~{maxLongTermDiscountRate}%{' '}
                  </span>
                </div>
              )}
              {hasEarlyCheckinDiscount && (
                <div className="space-x-1">
                  <span className="font-normal text-gray-900">
                    {t('room_detail.immediate_move_in')}
                  </span>
                  <span className="font-semibold text-black">
                    ~
                    {t('common.price_manwon', {
                      price: maxEarlyCheckinDiscountAmount || 0,
                    })}
                  </span>
                </div>
              )}
            </dd>
          </dl>
          <Icon
            name="arrow-bottom"
            size={18}
            className="text-black transition-transform duration-200 group-open:rotate-180"
          />
        </summary>

        <Discounts
          longTermDiscounts={longTermDiscounts}
          earlyCheckinDiscounts={earlyCheckinDiscounts}
          className="mt-2.5"
        />
      </details>
    </>
  )
}
